/**
 * EventCancelModal.tsx
 *
 * "Cancel this event?" confirmation modal.
 * Shows warning banner, event summary, CANCEL text verification.
 */

import React, { useState } from 'react';
import { FiX, FiAlertCircle, FiTrash2 } from 'react-icons/fi';
import { Button } from '../ui/Button';

interface EventCancelModalProps {
  eventTitle: string;
  eventDate: string;
  eventLocation: string;
  ticketsSold: number;
  eventImage?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;

  title?: string;
  description?: string;
  confirmWord?: string;
  confirmPlaceholder?: string;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmLoadingLabel?: string;
  confirmButtonClass?: string;
  variant?: 'cancel' | 'delete';
}

export const EventCancelModal: React.FC<EventCancelModalProps> = ({
  eventTitle,
  eventDate,
  eventLocation,
  ticketsSold,
  eventImage,
  onConfirm,
  onCancel,
  isLoading = false,

  title,
  description,
  confirmWord = 'CANCEL',
  confirmPlaceholder,
  cancelLabel,
  confirmLabel,
  confirmLoadingLabel,
  confirmButtonClass,
  variant = 'cancel',
}) => {
  const [confirmText, setConfirmText] = useState('');
  const canConfirm = confirmText.trim().toUpperCase() === confirmWord.toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
      <div className="w-full max-w-lg bg-zinc-950 border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl font-semibold">
            {title ?? (variant === 'delete' ? 'Delete this draft?' : 'Cancel this event?')}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 rounded-full hover:bg-white/5 transition-colors text-zinc-400 hover:text-white"
          >
            <FiX size={22} />
          </button>
        </div>

        {/* Warning icon */}
        <div className="flex justify-center mb-5">
          <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            {variant === 'delete' ? (
              <FiTrash2 size={28} className="text-red-400" />
            ) : (
              <FiX size={28} className="text-red-400" />
            )}
          </div>
        </div>

        {/* Description */}
        <p className="text-white text-center text-sm sm:text-base leading-relaxed mb-5">
          {description ??
            (variant === 'delete'
              ? 'This will permanently delete this draft and all its data.'
              : 'This will cancel the event for all attendees and prevent future ticket purchases')}
        </p>

        {/* Warning banner — only for cancel, not delete */}
        {variant !== 'delete' && ticketsSold > 0 && (
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-500/8 border border-amber-500/20 mb-5">
            <FiAlertCircle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 text-sm font-semibold">Tickets have already been sold</p>
              <p className="text-amber-300/80 text-xs mt-0.5">
                Attendees may require refunds after cancellation.
              </p>
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="h-px bg-white/5 mb-5" />

        {/* Event summary */}
        <div className="flex items-center gap-4 p-4 rounded-2xl bg-zinc-900/60 border border-white/8 mb-5">
          {eventImage && (
            <img src={eventImage} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold truncate">{eventTitle}</p>
            <p className="text-zinc-500 text-xs mt-0.5">
              {eventDate} · {eventLocation}
            </p>
            {ticketsSold > 0 && (
              <p className="text-zinc-400 text-xs mt-1">
                {ticketsSold.toLocaleString()} tickets sold
              </p>
            )}
          </div>
        </div>

        {/* Text input */}
        <div className="mb-6">
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={confirmPlaceholder ?? `Type ${confirmWord} to continue`}
            className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-all text-[14px]"
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1 rounded-full h-12 border-white/20 text-white hover:bg-white/5"
            onClick={onCancel}
          >
            {cancelLabel ?? (variant === 'delete' ? 'Keep Draft' : 'Keep Event')}
          </Button>
          <Button
            type="button"
            className={`flex-1 rounded-full h-12 disabled:opacity-30 ${
              confirmButtonClass ??
              (variant === 'delete'
                ? 'bg-red-600 text-white hover:bg-red-500'
                : 'bg-white text-black hover:bg-zinc-200')
            }`}
            onClick={onConfirm}
            disabled={!canConfirm || isLoading}
          >
            {isLoading
              ? (confirmLoadingLabel ?? (variant === 'delete' ? 'Deleting…' : 'Cancelling…'))
              : (confirmLabel ?? (variant === 'delete' ? 'Delete Draft' : 'Cancel Event'))}
          </Button>
        </div>
      </div>
    </div>
  );
};
